export enum UserRole {
  SUPER_ADMIN = 'Super Admin',
  ORGANIZATION_ADMIN = 'Organization Admin',
  DEVICE_GROUP_ADMIN = 'Device Group Admin',
  REGULAR_USER = 'Regular User',
  ORGANIZATION_VIEWER = 'Organization Viewer',
}

export enum UserAccessLevel {
  SUPER_ADMIN = 1,
  ORGANIZATION_ADMIN = 2,
  DEVICE_GROUP_ADMIN = 3,
  REGULAR_USER = 4,
  /** Staff read-grant; off the 1–4 member ladder. Gate with exact === 5. */
  ORGANIZATION_VIEWER = 5,
}

export interface User {
  UserID: string;
  FirstName?: string;
  LastName?: string;
  ContactEmail?: string;
  ContactPhone?: string;
  AccessLevel: UserAccessLevel;
  /** Home / legacy scalar organization. */
  OrganizationID?: string | null;
  /**
   * Org-admin ACL or Organization Viewer grant list (JWT claim + create/update body).
   * Valid when AccessLevel is ORGANIZATION_ADMIN or ORGANIZATION_VIEWER.
   */
  OrganizationIDs?: number[];
  /**
   * Orgs this user administers (users list). May be redacted to the
   * caller's ACL when the caller is an organization admin.
   */
  administeredOrganizations?: Organization[];
  /**
   * Orgs this Organization Viewer may read (users list). Super Admin only;
   * not returned to org-admin member lists.
   */
  viewedOrganizations?: Organization[];
  Timezone?: string;
  groups?: Array<{
    GroupID: string;
    GroupName: string;
  }>;
  limitRecordsByTime: boolean;
  recordWindowHours?: string;
  GroupIDs?: string;
}

export interface UserFilter {
  accessLevel: UserAccessLevel;
  role: UserRole;
}

export interface Organization {
  OrganizationID: number;
  Name: string;
  Domain?: string;
  Dnp3SqsTopicURL?: string;
  /** Freshsales account ID; null/omit when unset. */
  FreshsalesAccountId?: string | null;
  Transitional?: boolean;
  MaskFaultDetails?: boolean;
  AutoLockInactiveUsers?: boolean;
  RequireUppercase?: boolean;
  RequireLowercase?: boolean;
  RequireNumbers?: boolean;
  RequireSpecialCharacters?: boolean;
  MinimumPasswordLength?: number;
  PasswordHistoryCount?: number;
  CheckHaveIBeenPwned?: boolean;
  /** Org-wide MFA policy; authoritative source is GET /api/admin/organization/mfa-settings when needed. */
  RequireMfaForUsers?: boolean;
}

/** GET /api/admin/organization/mfa-settings */
export interface OrgMfaSettings {
  OrganizationID: number;
  Name: string;
  RequireMfaForUsers: boolean;
}

/** PATCH /api/admin/organization/mfa-settings */
export interface UpdateMfaSettingsResponse {
  success: boolean;
  organization: Pick<OrgMfaSettings, 'OrganizationID' | 'Name' | 'RequireMfaForUsers'>;
}

/** Response shape for GET account-lock-settings (OrganizationID may be string from API). */
export interface AccountLockSettings {
  OrganizationID: string;
  Name: string;
  AutoLockInactiveUsers: boolean;
}

export interface Group {
  GroupID: string;
  GroupName: string;
  OrganizationID: number;
  IsOrganizationDefault?: boolean;
  users?: User[];
  organization?: Organization;
  /** From groups list payload; used when opening Edit Group modal */
  ConfigTemplateId?: number | null;
  ConfigInheritanceMode?: 'SNAPSHOT' | 'CUSTOM';
  ConfigLockDevices?: boolean;
  EnableConnectivityAlerts?: boolean;
}
